import {
  Box,
  Table as ChakraTable,
  Checkbox,
  HStack,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Typography } from 'Tokens';
import { HelpTooltip, LearnMoreDrawer, MetricLearnMoreHeader, Select } from 'Molecules';
import { MetricTypeIcon, MetricTypes } from 'Molecules/MetricTypeIcon';
import { MetricNameWithTag } from 'containers/Esrs/pieces/DataCollection/DataCollectionUtils';
import { AdditionalTypesGroup } from 'Molecules/MetricTagsList';
import { EmptyState, IconButton, TruncatableText } from 'Atoms';
import { HelpIcon } from 'Tokens/Icons/Status';
import { Dispatch, MutableRefObject, SetStateAction, useMemo, useRef, useState } from 'react';
import { GetDrMaterialMetricsQuery_, QuestionType_Enum_ } from 'models';
import { useTranslation } from 'utils/translation';
import {
  MappedDisclosureRequirements,
  MaterialityState,
  MaterialityStatus,
  MetricMaterialityState,
  ParentMaterialMetrics,
  VSMEMaterialityStatus,
} from '../DoubleMaterialityAssessment.d';
import {
  COLLECT_ONLY_OPTIONS,
  MATERIALITY_LABELS,
  METRIC_MATERIALITY_OPTIONS,
  VSME_METRIC_MATERIALITY_OPTIONS,
} from './DrMaterialityUtils';
import {
  handleMetric,
  isParentMaterial,
  isParentMaterialForSub,
  isParentMetricOnGroupLevel,
} from './DrMateriality.hooks';
import { ActiveStepLabels } from 'containers/Esrs/EsrsUtilComponents';
import { Metric } from '../../MaterialityAssessment.d';
import { MaterialityStatusBox } from '../MaterialityUtils';
import { FrameworkRefs, Frameworks } from 'containers/Esrs';
import { useParams } from 'react-router-dom';
import { MetricsFilter } from './MetricsFilter';
import { uniqBy } from 'lodash';
import { RemoveIcon } from 'Tokens/Icons/Function';
import { MetricsMaterialityTableMetric } from './DrMateriality';

export const MetricsMaterialityTable = ({
  metrics,
  readOnly,
  disclosureRequirement,
  isGroup,
  hasParent,
  drMateriality,
  materialMetrics,
  parentMetrics,
  isCollectOnly,
  isOtherMetrics,
  borderLeftNone = false,
  selectedMetricsToUpdate,
  setSelectedMetricsToUpdate,
  handleUpdateMateriality,
}: {
  metrics: Metric[];
  readOnly: boolean;
  disclosureRequirement?: MappedDisclosureRequirements[number];
  isGroup: boolean;
  hasParent: boolean;
  drMateriality: MaterialityState | null;
  materialMetrics: GetDrMaterialMetricsQuery_['esrs_MaterialMetric'];
  parentMetrics: ParentMaterialMetrics;
  isCollectOnly: boolean;
  isOtherMetrics?: boolean;
  borderLeftNone?: boolean;
  selectedMetricsToUpdate: MetricsMaterialityTableMetric[];
  setSelectedMetricsToUpdate: Dispatch<SetStateAction<MetricsMaterialityTableMetric[]>>;
  handleUpdateMateriality: (
    selection: MetricMaterialityState,
    metric: Metric,
    isChild?: boolean
  ) => void;
}) => {
  const { t } = useTranslation('esrs');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { framework } = useParams();
  const metricNameRef = useRef<HTMLDivElement>(null);
  const [selectedMetric, setSelectedMetric] = useState<Metric>();
  const [conditions, setConditons] = useState<string[]>(['all']);
  const [clearFilters, setClearFilters] = useState<boolean>(false);

  const materialityFilterOptions = !isCollectOnly
    ? [
        {
          ref: 'all',
          label: 'All',
        },
        {
          ref: 'material',
          label: framework === FrameworkRefs.vsme ? 'Collect and report' : 'Material',
        },
        {
          ref: 'notMaterial',
          label: framework === FrameworkRefs.vsme ? 'Not collected' : 'Not material',
        },
        {
          ref: 'gatherData',
          label:
            framework === FrameworkRefs.vsme ? 'Only collect' : 'Not material but collect data',
        },
      ]
    : [
        {
          ref: 'all',
          label: 'All',
        },
        { ref: 'collectData', label: 'Collect data' },
        { ref: 'doNotCollect', label: 'Do not collect' },
      ];

  const showGroup = useMemo(
    () => !readOnly && !isGroup && hasParent,
    [readOnly, isGroup, hasParent]
  );
  const isDRAssessed = useMemo(
    () => drMateriality !== MaterialityState.toAssess && drMateriality !== null,
    [drMateriality]
  );
  const selectedMetricFromDR = useMemo(() => {
    return metrics.find((metric) => metric.reference === selectedMetric?.reference);
  }, [selectedMetric]);

  const selectOptions = useMemo(() => {
    if (isCollectOnly) {
      return COLLECT_ONLY_OPTIONS;
    }
    if (framework == 'vsme') {
      return VSME_METRIC_MATERIALITY_OPTIONS;
    }
    return METRIC_MATERIALITY_OPTIONS;
  }, [isCollectOnly]);

  const addMetrics = (metric?: MetricsMaterialityTableMetric) => {
    const childMetrics: MetricsMaterialityTableMetric[] = [];
    const addChildren = (metric?: MetricsMaterialityTableMetric) => {
      if (
        !metric?.isAssessable ||
        childMetrics.find((met) => met.reference === metric.reference) ||
        metric.materiality === MetricMaterialityState.collectDataMandatory
      ) {
        return;
      }

      childMetrics.push(metric);
      metric.childrenMetrics?.forEach((child) => {
        const childMetric = filteredDrMetrics.find((met) => child.childMetricRef === met.reference);
        if (childMetric) {
          addChildren(childMetric);
        }
      });
    };
    if (metric) {
      addChildren(metric);
    }
    return childMetrics;
  };

  const removeMetrics = (metric: MetricsMaterialityTableMetric) => {
    let metrics: MetricsMaterialityTableMetric[] = [...selectedMetricsToUpdate];
    const removeChildren = (metric: Metric) => {
      metrics = metrics.filter((met) => {
        if (met.reference === metric.reference) {
          return false;
        }
        return true;
      });
      metric.childrenMetrics?.forEach((child) => {
        removeChildren(child.childMetric as Metric);
      });
    };
    removeChildren(metric);
    return metrics;
  };

  const filterOptions = (metric: Metric) => {
    if (isCollectOnly) {
      return selectOptions;
    }
    return selectOptions.filter((option) => {
      const isParentMaterialMetric = isParentMaterial(
        metric.reference,
        parentMetrics,
        disclosureRequirement?.parentMateriality
      );

      if (
        isParentMaterialMetric &&
        !isParentMetricOnGroupLevel(metrics ?? [], parentMetrics, metric)
      ) {
        if (
          !metric?.additionalTypes?.some((additionalType) =>
            additionalType?.additionalType?.title?.includes('Phased')
          )
        )
          return (
            option.value !== MetricMaterialityState.notMaterial &&
            option.value !== MetricMaterialityState.phasedIn
          );
        return option.value !== MetricMaterialityState.notMaterial;
      }
      if (
        !metric?.additionalTypes?.some((additionalType) =>
          additionalType?.additionalType?.title?.includes('Phased')
        )
      )
        return option.value !== MetricMaterialityState.phasedIn;
      return true;
    });
  };

  const [drMetrics, someMetricsHaveAdditionalTypes] = useMemo(() => {
    return [
      metrics
        ?.map((metric) => {
          const { materiality, additionalTypes, metricRefNumber } = handleMetric(
            metric,
            materialMetrics,
            isDRAssessed,
            isCollectOnly,
            parentMetrics,
            disclosureRequirement?.parentMateriality
          );
          const metricOptions = filterOptions(metric);
          return {
            ...metric,
            materiality,
            metricAdditionalTypes: additionalTypes,
            metricRefNumber,
            metricOptions,
          };
        })
        .filter((metric) => metric.isAssessable === true),
      metrics?.some((metric) => !!metric.additionalTypes.length),
    ];
  }, [metrics, materialMetrics]);

  const filteredDrMetrics = useMemo(() => {
    return drMetrics.filter((metric) => {
      let additionalConditions = true;
      for (let i = 1; i < conditions.length; i++) {
        if (
          !metric.additionalTypes.find(
            (additionalType) => additionalType.additionalType?.reference === conditions[i]
          )
        ) {
          additionalConditions = false;
          break;
        }
      }
      return (
        (metric.materiality === conditions[0] ||
          conditions[0] === 'all' ||
          (conditions[0] === 'collectData' &&
            metric.materiality === MetricMaterialityState.collectDataMandatory)) &&
        additionalConditions
      );
    });
  }, [drMetrics, conditions]);

  const localSelectedMetrics = selectedMetricsToUpdate.filter((met) =>
    filteredDrMetrics.find((m) => m.reference === met.reference)
  );
  const isSomeMetricCollectData = !!filteredDrMetrics?.some(
    (met) => met.materiality !== MetricMaterialityState.collectDataMandatory
  );
  const additionalInfoFilterOptions = useMemo(() => {
    const filterOptions: {
      label: string;
      ref: string;
    }[] = [];
    drMetrics?.forEach((met) => {
      met.additionalTypes.forEach((additionalType) => {
        filterOptions.push({
          label: additionalType.additionalType?.title ?? '',
          ref: additionalType.additionalType?.reference,
        });
      });
    });
    return uniqBy(filterOptions, 'ref');
  }, []);

  const isCheckBoxColumnVisible =
    !readOnly &&
    (drMateriality !== MaterialityState.collectDataMandatory ||
      (drMateriality === MaterialityState.collectDataMandatory && isSomeMetricCollectData));

  return (
    <VStack
      pl="32px"
      alignItems="stretch"
      gap="8px"
      ml="14px"
      pb={isOtherMetrics ? '0px' : '32px'}
      borderLeft={isOtherMetrics || borderLeftNone ? 'none' : '1px dashed'}
      borderColor="border.hover"
      width={'100%'}
    >
      <HStack justifyContent={'space-between'}>
        <ActiveStepLabels
          title={isOtherMetrics ? 'Add other data points' : 'Assess metrics'}
          hasSteps={false}
          ml="70px"
          clickable={false}
        />
        <HStack>
          <MetricsFilter
            title={'Group materiality'}
            options={materialityFilterOptions}
            menuListWidth="222px"
            setConditions={setConditons}
            clearFilters={clearFilters}
            setClearFilters={setClearFilters}
          />
          <MetricsFilter
            title={'Additional information'}
            options={additionalInfoFilterOptions}
            isSingleSelect={false}
            setConditions={setConditons}
            clearFilters={clearFilters}
            setClearFilters={setClearFilters}
          />
        </HStack>
      </HStack>
      {filteredDrMetrics.length ? (
        <Box width="100%" border="1px solid" borderColor="border.decorative" borderRadius="8px">
          <ChakraTable width="100%" sx={{ tableLayout: 'fixed' }} variant="unstyled">
            <Thead borderBottom="1px solid" borderColor="border.decorative" borderRadius="8px">
              <Tr>
                {isCheckBoxColumnVisible && (
                  <Th w="50px">
                    {!readOnly && drMateriality !== MaterialityState.collectDataMandatory && (
                      <Checkbox
                        isChecked={localSelectedMetrics?.length === filteredDrMetrics?.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const metrics: MetricsMaterialityTableMetric[] = [];
                            filteredDrMetrics.forEach((met) => {
                              if (
                                !(met.materiality === MetricMaterialityState.collectDataMandatory)
                              )
                                metrics.push(...addMetrics(met));
                            });
                            setSelectedMetricsToUpdate(
                              uniqBy([...selectedMetricsToUpdate, ...metrics], 'reference')
                            );
                          } else {
                            setSelectedMetricsToUpdate((prev) => {
                              return uniqBy(
                                prev.filter(
                                  (met) =>
                                    !filteredDrMetrics.find(
                                      (fMet) => fMet.reference === met.reference
                                    )
                                ),
                                'reference'
                              );
                            });
                          }
                        }}
                        isIndeterminate={
                          localSelectedMetrics?.length !== filteredDrMetrics?.length &&
                          localSelectedMetrics?.length > 0
                        }
                      />
                    )}
                  </Th>
                )}

                <Th alignSelf="flex-start" p="12px" textTransform="none">
                  <Typography variant="bodyStrong">Metric</Typography>
                </Th>

                {someMetricsHaveAdditionalTypes && (
                  <Th w="184px" alignSelf="flex-start" p="12px" textTransform="none">
                    <Typography variant="bodyStrong">Information</Typography>
                  </Th>
                )}

                <Th p="5px 8px" w="70px" textTransform="none">
                  <Typography variant="bodyStrong">Ref.</Typography>
                </Th>

                {showGroup && (
                  <Th p="12px" w="128px" textTransform="none">
                    <HStack spacing="2px">
                      <Typography variant="bodyStrong">Group</Typography>
                      <HelpTooltip
                        label={t('assessment.materialityAssessment.table.tooltip.group')}
                      />
                    </HStack>
                  </Th>
                )}

                {!readOnly && (
                  <Th p="12px" w="184px" textTransform="none">
                    <HStack spacing="2px">
                      <Typography variant="bodyStrong">
                        {isGroup ? 'Group materiality' : 'Your company'}
                      </Typography>
                      {!readOnly && (
                        <HelpTooltip
                          label={
                            isGroup || !hasParent
                              ? t('assessment.materialityAssessment.table.tooltip.company')
                              : t('assessment.materialityAssessment.table.tooltip.subsidiary')
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                )}
                <Th w="24px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredDrMetrics?.map((metric, index) => {
                const metricFrameworkRef = metric?.requirement?.group?.standard?.frameworkRef;
                const { materiality, metricAdditionalTypes, metricRefNumber } = metric;
                return (
                  <Tr
                    key={index}
                    borderBottom={index + 1 < drMetrics.length ? '1px solid' : 'none'}
                    borderColor="border.decorative"
                  >
                    {isCheckBoxColumnVisible && (
                      <Td w="50px">
                        {materiality !== MetricMaterialityState.collectDataMandatory && (
                          <Checkbox
                            isChecked={
                              !!selectedMetricsToUpdate?.find(
                                (met) => met.reference === metric.reference
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const metricsToAdd = addMetrics(metric);
                                setSelectedMetricsToUpdate((prev) =>
                                  uniqBy([...prev, ...metricsToAdd], 'reference')
                                );
                              } else {
                                const metrics = removeMetrics(metric);
                                setSelectedMetricsToUpdate(uniqBy(metrics, 'reference'));
                              }
                            }}
                          />
                        )}
                      </Td>
                    )}
                    <Td p="12px">
                      <HStack spacing="2px" justifyContent={'left'}>
                        <HStack flex={1}>
                          <MetricTypeIcon
                            type={
                              metric.metricType === QuestionType_Enum_.Decimal_
                                ? MetricTypes.number
                                : MetricTypes.text
                            }
                          />
                          <Box ref={metricNameRef} width="100%">
                            <MetricNameWithTag
                              name={metric.shortTitle ?? metric.title}
                              tags={metric.tags.map((tag) => tag.type)}
                              rowRef={metricNameRef as MutableRefObject<HTMLDivElement>}
                            />
                          </Box>
                        </HStack>
                      </HStack>
                    </Td>

                    {someMetricsHaveAdditionalTypes && (
                      <Td p="12px" w="128px">
                        <HStack spacing="2px">
                          <AdditionalTypesGroup tagsWithHelp={metricAdditionalTypes} />
                        </HStack>
                      </Td>
                    )}

                    <Td p="5px 8px">
                      <TruncatableText
                        variant="body"
                        text={
                          isOtherMetrics && metricFrameworkRef !== framework
                            ? `${metricFrameworkRef} ${metricRefNumber}`
                            : metricRefNumber
                        }
                      />
                    </Td>

                    {showGroup && (
                      <Td p="12px" w="128px">
                        <HStack spacing="2px">
                          <Typography variant="body">
                            {parentMetrics?.some((m) => {
                              return (
                                m.metricRef === metric.reference &&
                                m.isMaterial &&
                                m.isDataGatheringOnly &&
                                m.isPhasedIn
                              );
                            })
                              ? 'Phased-in'
                              : parentMetrics?.some((m) => {
                                    return (
                                      m.metricRef === metric.reference &&
                                      m.isMaterial &&
                                      m.isDataGatheringOnly
                                    );
                                  })
                                ? 'Collect data'
                                : !parentMetrics.find((m) => m.metricRef === metric.reference)
                                  ? MaterialityStatus.toAssess
                                  : isParentMaterial(
                                        metric.reference,
                                        parentMetrics,
                                        disclosureRequirement?.parentMateriality
                                      )
                                    ? MaterialityStatus.material
                                    : MaterialityStatus.notMaterial}
                          </Typography>
                        </HStack>
                      </Td>
                    )}

                    {!readOnly && (
                      <Td p="5px 8px">
                        <Box w="100%">
                          {materiality !== MetricMaterialityState.collectDataMandatory ? (
                            <Select
                              size="sm"
                              isDisabled={
                                !isDRAssessed ||
                                (materiality === MetricMaterialityState.collectData &&
                                  isParentMaterialForSub(
                                    metric.reference,
                                    parentMetrics,
                                    disclosureRequirement?.parentMateriality
                                  ))
                              }
                              isClearable={!isOtherMetrics}
                              options={metric.metricOptions}
                              isOptionDisabled={(option) => {
                                return !isCollectOnly
                                  ? ((option.value === MetricMaterialityState.material ||
                                      option.value === MetricMaterialityState.phasedIn) &&
                                      drMateriality === MaterialityState.gatherData) ||
                                      (option.value === MetricMaterialityState.material &&
                                        drMateriality === MaterialityState.phasedIn &&
                                        metric.additionalTypes.some((additionalType) =>
                                          additionalType?.additionalType?.title?.includes('Phased')
                                        ))
                                  : false;
                              }}
                              value={{
                                label:
                                  framework === FrameworkRefs.vsme &&
                                  VSMEMaterialityStatus[
                                    materiality as keyof typeof VSMEMaterialityStatus
                                  ]
                                    ? VSMEMaterialityStatus[
                                        materiality as keyof typeof VSMEMaterialityStatus
                                      ]
                                    : MATERIALITY_LABELS[materiality],
                                value: materiality as MetricMaterialityState,
                              }}
                              onChange={(selection) => {
                                if (selection?.value !== materiality) {
                                  handleUpdateMateriality(
                                    selection?.value ?? MetricMaterialityState.toAssess,
                                    metric
                                  );
                                }
                              }}
                              // deselect={handleDeselect}
                            />
                          ) : (
                            <MaterialityStatusBox
                              materiality={MaterialityState.collectDataMandatory}
                            />
                          )}
                        </Box>
                      </Td>
                    )}

                    <Td w="12px" p="10px 4px">
                      <IconButton
                        aria-label="learn more"
                        variant="ghost"
                        icon={<HelpIcon />}
                        size="md"
                        onClick={() => {
                          setSelectedMetric(metric);
                          onOpen();
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </ChakraTable>
          <LearnMoreDrawer
            isOpen={isOpen}
            onClose={onClose}
            description={selectedMetric?.description ?? ''}
            header={!selectedMetric ? disclosureRequirement?.title : undefined}
            customHeader={
              selectedMetric ? (
                <MetricLearnMoreHeader
                  metricRef={selectedMetric?.reference ?? ''}
                  tags={selectedMetricFromDR?.tags ?? []}
                  frameworkName={
                    Frameworks[
                      selectedMetric?.requirement?.group?.standard
                        ?.frameworkRef as keyof typeof FrameworkRefs
                    ]
                  }
                />
              ) : undefined
            }
          />
        </Box>
      ) : (
        <Box>
          <EmptyState
            title={'Nothing found'}
            description={"We couldn't find anything that match your filters. Try to remove filters"}
            callToAction={{
              text: 'Clear all filters',
              variant: 'secondary',
              onClick: () => {
                setConditons(['all']);
                setClearFilters(true);
              },
              leftIcon: <RemoveIcon color={'text.action'} />,
            }}
            component={true}
          ></EmptyState>
        </Box>
      )}
    </VStack>
  );
};
